import { useEffect, useRef, useState } from "react";

export interface Series { key: string; label: string; color: string }
export interface StackedBar { label: string; full: string; values: Record<string, number> }

/**
 * StackedBars draws a few parts of one total per period (one axis), with a
 * legend, a hover tooltip and a table view. Colors are fixed per series and
 * were checked for colour-blind separation on the dark panel.
 */
export function StackedBars({ title, series, bars, height = 190 }: { title: string; series: Series[]; bars: StackedBar[]; height?: number }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(560);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => { ro.disconnect(); };
  }, [table]);
  const sum = (b: StackedBar) => series.reduce((s, x) => s + (b.values[x.key] ?? 0), 0);
  const top = niceMax(Math.max(...bars.map(sum), 0));
  const pad = { l: 36, r: 6, t: 10, b: 22 };
  const plotW = w - pad.l - pad.r;
  const plotH = height - pad.t - pad.b;
  const slot = plotW / Math.max(bars.length, 1);
  const barW = Math.max(2, Math.min(28, slot - 2));
  const y = (v: number) => pad.t + plotH - (top ? (v / top) * plotH : 0);
  const every = Math.ceil(bars.length / Math.max(2, Math.floor(plotW / 64)));
  const totals = Object.fromEntries(series.map((s) => [s.key, bars.reduce((t, b) => t + (b.values[s.key] ?? 0), 0)]));
  const h = hover != null ? bars[hover] : null;
  const tipX = hover != null ? Math.min(Math.max(pad.l + hover * slot + slot / 2, 90), w - 90) : 0;

  return (
    <figure className="chart stacked">
      <figcaption>
        <b>{title}</b>
        <button type="button" className="link small" onClick={() => setTable(!table)}>{table ? "Show chart" : "Show table"}</button>
      </figcaption>
      <ul className="legend" aria-label="Legend">
        {series.map((s) => (
          <li key={s.key}><i style={{ background: s.color }} aria-hidden />{s.label} <b>{totals[s.key].toLocaleString()}</b></li>
        ))}
      </ul>
      {table ? (
        <table className="table small">
          <thead><tr><th>Period</th>{series.map((s) => <th key={s.key} className="num">{s.label}</th>)}</tr></thead>
          <tbody>{bars.map((b, i) => <tr key={i}><td>{b.full}</td>{series.map((s) => <td key={s.key} className="num">{(b.values[s.key] ?? 0).toLocaleString()}</td>)}</tr>)}</tbody>
        </table>
      ) : (
        <div ref={box} className="chart-box">
          <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} role="img"
            aria-label={`${title}: ${series.map((s) => `${s.label} ${totals[s.key]}`).join(", ")}`} onMouseLeave={() => setHover(null)}>
            {[0, 0.5, 1].map((f) => (
              <g key={f}>
                <line x1={pad.l} x2={w - pad.r} y1={y(top * f)} y2={y(top * f)} className="chart-grid" />
                <text x={pad.l - 6} y={y(top * f) + 4} textAnchor="end" className="chart-tick">{compact(top * f)}</text>
              </g>
            ))}
            {bars.map((b, i) => {
              const x = pad.l + i * slot + (slot - barW) / 2;
              let base = 0;
              const parts = series.map((s) => ({ s, v: b.values[s.key] ?? 0 })).filter((p) => p.v > 0);
              return (
                <g key={i} opacity={hover == null || hover === i ? 1 : 0.45}>
                  {parts.map((p, j) => {
                    const y0 = y(base), y1 = y(base + p.v);
                    base += p.v;
                    const last = j === parts.length - 1;
                    // 2px surface gap between stacked parts; the top end is rounded.
                    const hgt = Math.max(0, y0 - y1 - (last ? 0 : 2));
                    const r = last ? Math.min(4, barW / 2, hgt) : 0;
                    const top1 = y0 - hgt;
                    return (
                      <path key={p.s.key} fill={p.s.color}
                        d={`M${x},${y0} V${top1 + r} Q${x},${top1} ${x + r},${top1} H${x + barW - r} Q${x + barW},${top1} ${x + barW},${top1 + r} V${y0} Z`} />
                    );
                  })}
                  <rect x={pad.l + i * slot} y={pad.t} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
                  {i % every === 0 && <text x={x + barW / 2} y={height - 6} textAnchor="middle" className="chart-tick">{b.label}</text>}
                </g>
              );
            })}
            <line x1={pad.l} x2={w - pad.r} y1={pad.t + plotH} y2={pad.t + plotH} className="chart-axis" />
          </svg>
          {h && (
            <div className="chart-tip" style={{ left: tipX }} role="status">
              <b>{h.full}</b>
              {series.map((s) => <span key={s.key}><i style={{ background: s.color }} aria-hidden />{s.label}<b>{(h.values[s.key] ?? 0).toLocaleString()}</b></span>)}
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

function compact(v: number): string {
  if (v >= 1e6) return `${+(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${+(v / 1e3).toFixed(1)}k`;
  return `${+v.toFixed(v < 10 ? 1 : 0)}`;
}
